import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { requireAnyPermission } from '@/lib/permissions/permissions'
import { store } from '@/lib/states/store'
import VaultPage from '@/components/vault/VaultPage'
import { useOrgEntitlements } from '@/lib/hooks/useOrgEntitlements'
import { canUseVault } from '@/lib/billing/plans'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { invalidateCoachOnboardingQueries } from '@/lib/utils/onboardingCache'
import { trpc } from '@/lib/trpc/client'

export const Route = createFileRoute('/_auth/_dashboard/vault')({
  beforeLoad: () => {
    requireAnyPermission(['vault.org.read', 'vault.org.edit'])
  },
  loader: () => {
    const org = store.getState().session.org
    if (!org) {
      throw new Error('Org not found')
    }
    return { org }
  },
  component: VaultRoutePage,
})

function VaultRoutePage() {
  const { org } = Route.useLoaderData()
  const { data: entitlements } = useOrgEntitlements()
  const allowVault = canUseVault(entitlements)
  const queryClient = useQueryClient()
  const { mutateAsync: completeOnboardingItem } =
    trpc.onboarding.completeItem.useMutation()
  const markedResourceViewRef = useRef(false)

  useEffect(() => {
    if (!allowVault || markedResourceViewRef.current) return
    markedResourceViewRef.current = true

    const markViewed = async () => {
      try {
        await completeOnboardingItem({
          key: 'coach_onboarding',
          version: 1,
          itemKey: 'setup.resource.view',
          source: 'auto',
          metadata: { source: 'vault-route-view' },
        })
        await invalidateCoachOnboardingQueries(queryClient)
      } catch (error) {
        markedResourceViewRef.current = false
        console.warn(
          'Failed to complete onboarding item setup.resource.view',
          error,
        )
      }
    }

    void markViewed()
  }, [allowVault, completeOnboardingItem, queryClient])

  if (!allowVault) {
    return (
      <div className="h-full w-full p-4 md:p-8">
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Vault requires Growth</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Upgrade to Growth or above to access your resource vault and
              client vault sharing.
            </p>
            <Button asChild size="sm">
              <Link to="/upgrade" search={{ plan: 'growth_monthly' }}>
                View upgrade options
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="h-full w-full">
      <div className="flex h-full min-h-0 w-full flex-col md:flex">
        <div className="flex h-full min-h-0 w-full flex-1 px-4 py-4 md:p-8">
          <VaultPage orgId={org.id} scope="org" title="Vault" />
        </div>
      </div>
    </div>
  )
}
